import type { ReactNode } from 'react';
import { JourneyShell } from './JourneyShell.js';
import { CUSTOMER_CONTENT_NARROW_CLASS } from '../../app/customerTopBarStyles.js';
import { CustomerSubpageBackBar } from '../../shared/ui/CustomerSubpageBackBar.js';

interface DonationStepLayoutProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly embeddedInAccount: boolean;
  readonly children: ReactNode;
  /** Named parent path for ←; omit to auto-resolve. `null` hides unless onBack set. */
  readonly backTo?: string | null;
  readonly onBack?: () => void;
  readonly backLabel?: string;
}

/**
 * Guest donate uses JourneyShell; logged-in account donate renders inside AccountShell outlet.
 * Embedded account: AccountShell omits its auto-bar; DonationStepLayout owns content ←,
 * which W2-suppresses when fixed shell already owns depth/step ←.
 * Step onBack / backTo come from PhoneFirstDonationScreenView and intentionally mirror
 * the same-URL onStepBack / cross-route stepBackTo publish (not a second policy).
 *
 * Content width: CUSTOMER_CONTENT_NARROW_CLASS (donation forms — not shell-wide).
 */
export function DonationStepLayout({
  title,
  subtitle,
  embeddedInAccount,
  children,
  backTo,
  onBack,
  backLabel,
}: DonationStepLayoutProps): JSX.Element {
  if (embeddedInAccount) {
    return (
      <div
        className={`${CUSTOMER_CONTENT_NARROW_CLASS} flex flex-col gap-4`}
        data-testid="account-donation-step"
      >
        <CustomerSubpageBackBar
          to={backTo}
          onBack={onBack}
          label={backLabel}
          testId="customer-donation-hierarchical-back"
        />
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">{title}</h2>
          {subtitle !== undefined ? (
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">{subtitle}</p>
          ) : null}
        </div>
        {children}
      </div>
    );
  }

  return (
    <JourneyShell
      title={title}
      subtitle={subtitle}
      context="PHONE_FIRST_DONATION"
      contentWidth="narrow"
      backTo={backTo}
      onBack={onBack}
      backLabel={backLabel}
    >
      {children}
    </JourneyShell>
  );
}
